import { useCallback, useEffect, useRef, useState, type RefObject } from "react";

const DEFAULT_THRESHOLD = 56;
const DEFAULT_MAX_PULL = 96;

const PULL_REFRESH_IGNORE_SELECTOR =
  'button, a, input, textarea, select, [role="button"], [data-no-pull-refresh]';

function isPullRefreshIgnoredTarget(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest(PULL_REFRESH_IGNORE_SELECTOR) != null
  );
}

export interface UsePullToRefreshOptions {
  scrollRef: RefObject<HTMLElement | null>;
  onRefresh: () => void | Promise<void>;
  disabled?: boolean;
  threshold?: number;
  maxPull?: number;
}

export interface UsePullToRefreshResult {
  pullDistance: number;
  isRefreshing: boolean;
  pullProgress: number;
}

/** Touch / pointer pull-down gesture for refreshing scrollable lists. */
export function usePullToRefresh({
  scrollRef,
  onRefresh,
  disabled = false,
  threshold = DEFAULT_THRESHOLD,
  maxPull = DEFAULT_MAX_PULL,
}: UsePullToRefreshOptions): UsePullToRefreshResult {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const pulling = useRef(false);
  const pullDistanceRef = useRef(0);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    pullDistanceRef.current = pullDistance;
  }, [pullDistance]);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const canStartPull = useCallback(() => {
    const el = scrollRef.current;
    if (!el || disabled || isRefreshing) return false;
    return el.scrollTop <= 0;
  }, [scrollRef, disabled, isRefreshing]);

  const finishPull = useCallback(async () => {
    if (!pulling.current) return;
    pulling.current = false;

    const shouldRefresh = pullDistanceRef.current >= threshold;
    if (!shouldRefresh) {
      setPullDistance(0);
      return;
    }

    setIsRefreshing(true);
    setPullDistance(threshold);
    try {
      await Promise.resolve(onRefreshRef.current());
    } finally {
      setIsRefreshing(false);
      setPullDistance(0);
    }
  }, [threshold]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || disabled) return;

    const onTouchStart = (event: TouchEvent) => {
      if (isPullRefreshIgnoredTarget(event.target)) return;
      if (!canStartPull()) return;
      startY.current = event.touches[0]?.clientY ?? 0;
      pulling.current = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!pulling.current) return;
      if (!canStartPull()) {
        pulling.current = false;
        setPullDistance(0);
        return;
      }

      const currentY = event.touches[0]?.clientY ?? startY.current;
      const delta = currentY - startY.current;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }

      event.preventDefault();
      setPullDistance(Math.min(delta * 0.45, maxPull));
    };

    const onTouchEnd = () => {
      void finishPull();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.button !== 0) return;
      if (isPullRefreshIgnoredTarget(event.target)) return;
      if (!canStartPull()) return;
      startY.current = event.clientY;
      pulling.current = true;
      el.setPointerCapture(event.pointerId);
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!pulling.current || event.pointerType === "touch") return;
      if (!canStartPull()) {
        pulling.current = false;
        setPullDistance(0);
        return;
      }

      const delta = event.clientY - startY.current;
      if (delta <= 0) {
        setPullDistance(0);
        return;
      }

      event.preventDefault();
      setPullDistance(Math.min(delta * 0.45, maxPull));
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerType === "touch") return;
      if (el.hasPointerCapture(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
      void finishPull();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd);
    el.addEventListener("touchcancel", onTouchEnd);
    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", onPointerUp);
    el.addEventListener("pointercancel", onPointerUp);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", onPointerUp);
      el.removeEventListener("pointercancel", onPointerUp);
    };
  }, [scrollRef, disabled, canStartPull, finishPull, maxPull]);

  const pullProgress = Math.min(1, pullDistance / threshold);

  return { pullDistance, isRefreshing, pullProgress };
}
